import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { tplApi } from "@/lib/templateApi";

export default function AdminLogin() {
  const { slug } = useParams();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const res = await tplApi.requestMagicLink(email, slug);
      setSent(true);
      if (res.magic_link) setDevLink(res.magic_link);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to={`/t/${slug}`} className="text-lg font-extrabold text-white">
            Site<span className="text-slate-500 font-medium">Smith</span>
          </Link>
          <p className="text-slate-500 text-sm mt-2">Admin sign-in</p>
        </div>

        {sent ? (
          <div className="text-center" data-testid="magic-link-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={28} className="text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Check your email</h2>
            <p className="text-slate-400 text-sm mb-6">
              We sent a sign-in link to <span className="text-white font-medium">{email}</span>
            </p>
            {devLink && (
              <div className="bg-[#1E293B] border border-white/10 rounded-lg p-4 text-left" data-testid="dev-magic-link">
                <p className="text-xs text-slate-500 mb-1 font-mono">DEV MODE — click to sign in:</p>
                <a
                  href={devLink}
                  className="text-orange-400 text-xs font-mono break-all hover:underline"
                >
                  {devLink}
                </a>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-900/20 border border-red-800/30 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <div>
              <label className="text-slate-400 text-sm block mb-1.5">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  className="pl-10 bg-[#1E293B] border-white/10 text-white placeholder:text-slate-600 h-12 rounded-lg focus:ring-orange-500"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Send sign-in link <ArrowRight size={16} /></>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
